import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, ShoppingBag, Warehouse, BookOpen, X, LogOut,
  Settings, Headphones, Truck, Megaphone, Code, MoreHorizontal, ClipboardList,
  ChevronRight, Calendar, Store, Plus,
} from 'lucide-react';
import { useConfirm } from '../../components/ConfirmModal';
import AppFooter from '../../components/AppFooter';
import { PageSkeletonFallback } from '../LoadingSkeleton';
import { db, doc, cachedGetDoc, collection, query, where, onSnapshot } from '../../services/firebase';
import { APP_CONFIG } from '../../utils/config';
import logo from '../../assets/logo.png';
import adminAvatar from '../../assets/avatar-admin.svg';

import { createPortal } from 'react-dom';

const sidebarLinks = [
  { to: '/admin',               label: 'Dashboard',    Icon: LayoutDashboard, end: true },
  { to: '/admin/retailers',     label: 'Retailers',    Icon: Users },
  { to: '/admin/daily-ledger',  label: 'Daily Sheet',  Icon: ShoppingBag },
  { to: '/admin/company-order', label: 'Company Order',Icon: Truck },
  { to: '/admin/inventory',     label: 'Inventory',    Icon: Warehouse },
  { to: '/admin/ledger',        label: 'Ledger',       Icon: BookOpen },
  { to: '/admin/support',       label: 'Tickets',      Icon: Headphones },
  { to: '/admin/place-order',   label: 'Place Order',  Icon: ClipboardList },
  { to: '/admin/announcements', label: 'Announcements',Icon: Megaphone },
  { to: '/admin/settings',      label: 'Settings',     Icon: Settings },
];

const bottomNavLinks = [
  { to: '/admin',              label: 'Home',    Icon: LayoutDashboard, end: true },
  { to: '/admin/daily-ledger', label: 'Daily',   Icon: ShoppingBag },
  { to: '/admin/company-order',label: 'Company', Icon: Truck },
  { to: '/admin/ledger',       label: 'Ledger',  Icon: BookOpen },
];

const moreCategories = [
  {
    category: 'Overview & Dispatch',
    links: [
      { to: '/admin',              label: 'Dashboard',     desc: 'Live operations & KPIs',        Icon: LayoutDashboard, end: true },
      { to: '/admin/daily-ledger', label: 'Daily Sheet',   desc: 'Morning dispatch & crates',     Icon: ShoppingBag },
      { to: '/admin/company-order',label: 'Company Order', desc: 'Plant procurement & stock-in',  Icon: Truck },
      { to: '/admin/ledger',       label: 'Ledger',        desc: 'Accounts, dues & billing',      Icon: BookOpen },
    ],
  },
  {
    category: 'Operations & Stock',
    links: [
      { to: '/admin/retailers',   label: 'Retailers',     desc: 'Accounts & outstanding dues',   Icon: Users },
      { to: '/admin/place-order', label: 'Place Order',   desc: 'Manual order dispatch',         Icon: ClipboardList },
      { to: '/admin/inventory',   label: 'Inventory',     desc: 'Stock levels & catalogs',        Icon: Warehouse },
    ],
  },
  {
    category: 'Communications',
    links: [
      { to: '/admin/support',       label: 'Support Tickets', desc: 'Queries & retailer issues', Icon: Headphones },
      { to: '/admin/announcements', label: 'Announcements',   desc: 'Network broadcasts',        Icon: Megaphone },
    ],
  },
  {
    category: 'Administration',
    links: [
      { to: '/admin/settings', label: 'Settings', desc: 'System rules & preferences', Icon: Settings },
    ],
  },
];

const moreLinks = moreCategories.flatMap(c => c.links);

const PAGE_TITLES = {
  '/admin': 'Dashboard',
  '/admin/retailers': 'Retailers',
  '/admin/daily-ledger': 'Daily Sheet',
  '/admin/company-order': 'Company Order',
  '/admin/inventory': 'Inventory',
  '/admin/ledger': 'Ledger',
  '/admin/support': 'Support Tickets',
  '/admin/place-order': 'Place Order',
  '/admin/announcements': 'Announcements',
  '/admin/settings': 'Settings',
};

const bannerColors = {
  urgent:  { wrap: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900',    label: 'text-red-600',   icon: 'text-red-600' },
  warning: { wrap: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900', label: 'text-amber-600', icon: 'text-amber-600' },
  success: { wrap: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900', label: 'text-green-700', icon: 'text-green-700' },
  info:    { wrap: 'bg-navy-50 border-navy-200 dark:bg-navy-950/30 dark:border-navy-900',  label: 'text-navy-700',  icon: 'text-navy-700' },
};

export default function AdminLayout() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const closeDrawer = () => setMoreOpen(false);
  const [navHidden, setNavHidden] = useState(false);
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(localStorage.getItem('lg_banner_dismissed') || '');
  const [unresolvedTickets, setUnresolvedTickets] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const user = JSON.parse(localStorage.getItem('lg_user') || '{}');
  const isDev = user.phone === '8051725780';
  const operatorName = (user.role === 'admin' && (user.name === 'Lal Babu Gupta' || !user.name))
    ? 'Ashish Prakash'
    : (user.name || 'Ashish Prakash');

  const pageTitle = (() => {
    const path = location.pathname;
    if (PAGE_TITLES[path]) return PAGE_TITLES[path];
    if (path.startsWith('/admin/orders/')) return 'Order Detail';
    const segment = path.split('/').pop();
    return segment.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  })();

  const currentLink = sidebarLinks.find(l => l.to === location.pathname) || moreLinks.find(l => l.to === location.pathname);
  const PageIcon = currentLink?.Icon || LayoutDashboard;

  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  useEffect(() => {
    import('../TopProgressBar').then(m => { m.triggerProgress(); setTimeout(m.stopProgress, 300); });
  }, [location.pathname]);

  useEffect(() => {
    if (isDev) {
      const stored = sessionStorage.getItem('lg_dev_verified');
      if (!stored || JSON.parse(stored).expiry <= Date.now()) window.location.href = '/dev';
    }
  }, [isDev]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (moreOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [moreOpen]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await cachedGetDoc(doc(db, 'settings', 'banner'), 10 * 60 * 1000);
        if (snap.exists()) {
          const data = snap.data();
          if (data.active && data.message && (!data.expiresAt || new Date(data.expiresAt) > new Date())) {
            if (data.target === 'all' || data.target === 'admin') {
              setBanner(bannerDismissed === data.createdAt ? null : data);
            } else setBanner(null);
          } else setBanner(null);
        } else setBanner(null);
      } catch {}
    })();
  }, [bannerDismissed]);

  useEffect(() => {
    let lastY = 0;
    const h = () => { const y = window.scrollY; setNavHidden(y > lastY && y > 60); lastY = y; };
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Real-time listener for unresolved support tickets count
  useEffect(() => {
    let unsub = () => {};
    try {
      const q = query(
        collection(db, 'support_tickets'),
        where('status', 'in', ['Open', 'In Progress', 'open', 'in progress'])
      );
      unsub = onSnapshot(q, (snap) => {
        setUnresolvedTickets(snap.size);
      }, (err) => {
        console.warn('Support tickets count listener warning:', err);
      });
    } catch (e) {
      console.warn('Failed to listen to support tickets:', e);
    }
    return () => unsub();
  }, []);

  const handleLogout = async () => {
    setMoreOpen(false);
    await new Promise(r => setTimeout(r, 300));
    const ok = await confirm({ title: 'Logout', message: 'Are you sure you want to logout from admin panel?', confirmText: 'Logout', type: 'logout' });
    if (ok) {
      const phone = sessionStorage.getItem('lg_active_phone');
      if (phone) { localStorage.removeItem(`lg_user_${phone}`); localStorage.removeItem(`lg_last_verify_${phone}`); }
      localStorage.removeItem('lg_user'); sessionStorage.removeItem('lg_active_phone'); window.location.reload();
    }
  };

  const bc = bannerColors[banner?.type] || bannerColors.info;

  return (
    <div className="min-h-screen flex bg-[#faf9f6] dark:bg-[#11100f] text-[#1a1917] dark:text-[#f5f4f0]">

      {/* ── Desktop Sidebar ── */}
      <aside
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
        className={`hidden lg:flex fixed top-0 left-0 h-full z-50 flex-col bg-[#0c1524] dark:bg-[#090f1a] border-r border-[#1a283e] dark:border-[#131d2e] transition-all duration-200 ${sidebarExpanded ? 'w-[220px]' : 'w-[60px]'}`}>

        {/* Logo */}
        <div className="h-[54px] flex items-center px-3.5 border-b border-white/10 shrink-0">
          <img src={logo} alt="Lucy Garden" className="w-7 h-7 rounded-lg object-cover shrink-0 border border-white/10" />
          {sidebarExpanded && (
            <div className="ml-2.5 overflow-hidden">
              <p className="font-bold text-white text-sm whitespace-nowrap leading-tight">Lucy Garden</p>
              <p className="text-white/40 text-[10px] whitespace-nowrap font-medium">Admin Portal</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
          {sidebarLinks.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} title={link.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all duration-150 relative ${
                  isActive
                    ? 'bg-[#1e3a5f] text-white shadow-xs'
                    : 'text-[#94a3b8] hover:bg-white/5 hover:text-white'
                }`}>
              {({ isActive }) => {
                const isSupport = link.to === '/admin/support';
                const hasUnresolved = isSupport && unresolvedTickets > 0;
                return (
                  <>
                    <div className="relative shrink-0 flex items-center justify-center">
                      <link.Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} className="shrink-0" />
                      {!sidebarExpanded && hasUnresolved && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-[#0c1524] animate-pulse" />
                      )}
                    </div>
                    {sidebarExpanded && (
                      <div className="flex items-center justify-between flex-1 min-w-0">
                        <span className="whitespace-nowrap">{link.label}</span>
                        {hasUnresolved && (
                          <span className="px-1.5 py-0.5 text-[10px] font-black rounded-full bg-amber-500 text-white font-mono shadow-xs animate-pulse leading-none">
                            {unresolvedTickets > 99 ? '99+' : unresolvedTickets}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </NavLink>
          ))}
          {isDev && (
            <button onClick={() => { window.location.href = '/dev'; }} title="Dev Panel"
              className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs font-semibold text-emerald-400 hover:bg-white/5 transition-colors">
              <Code size={16} strokeWidth={1.8} className="shrink-0" />
              {sidebarExpanded && <span className="whitespace-nowrap">Dev Panel</span>}
            </button>
          )}
        </nav>

        {/* User + Logout */}
        {sidebarExpanded ? (
          <div className="p-2 mx-2 mb-3 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-white/20 bg-white/10 flex items-center justify-center shadow-xs">
                <img src={adminAvatar} alt="Admin" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white truncate">{user.name || 'Admin'}</p>
                <p className="text-[10px] text-white/40">Owner · Dairy Console</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-500/15 text-red-300 text-[11px] font-bold hover:bg-red-500/25 transition-colors border border-red-500/20">
              <LogOut size={12} /> Logout
            </button>
          </div>
        ) : (
          <div className="px-2 mb-3">
            <button onClick={handleLogout} title="Logout"
              className="w-full flex items-center justify-center py-2 rounded-lg bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors border border-red-500/20">
              <LogOut size={15} />
            </button>
          </div>
        )}
      </aside>

      {/* ── Main Content ── */}
      <div className={`flex-1 min-w-0 transition-all duration-200 ${sidebarExpanded ? 'lg:ml-[220px]' : 'lg:ml-[60px]'} flex flex-col min-h-screen`}>

        {/* Mobile Header: Sleek, lightweight, no clutter */}
        <header className="sticky top-0 z-40 lg:hidden bg-[#0c1524] text-white border-b border-[#1a283e] shadow-xs" id="mobile-header">
          <div className="flex items-center justify-between px-3.5 h-[48px]">
            <Link to="/admin" className="flex items-center gap-2 min-w-0">
              <img src={logo} alt="Lucy Garden" className="w-7 h-7 rounded-md object-cover ring-1 ring-white/15 shrink-0" />
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-bold text-white text-xs tracking-tight truncate">Lucy Garden</span>
                <span className="text-[9px] font-bold uppercase tracking-wider bg-[#1e3a5f] text-blue-200 px-1.5 py-0.2 rounded border border-blue-400/20 shrink-0">
                  Admin
                </span>
              </div>
            </Link>

            <span className="text-[11px] font-mono text-white/60 shrink-0">
              {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        </header>

        {/* Desktop Header: Structured Enterprise System Bar */}
        <header className="sticky top-0 z-40 hidden lg:flex items-center justify-between px-6 h-[54px] bg-white dark:bg-[#161514] border-b border-[#dedbd5] dark:border-[#2a2826] shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          {/* Left: Active Section Icon & Breadcrumb */}
          <div className="flex items-center gap-2.5">
            <PageIcon size={17} className="text-[#1e3a5f] dark:text-blue-400 shrink-0" strokeWidth={2.2} />
            <div className="flex items-center gap-2 text-xs">
              <Link to="/admin" className="text-[#78716c] hover:text-[#1a1917] dark:text-[#8c887e] dark:hover:text-[#f5f4f0] transition-colors font-medium">
                Admin
              </Link>
              <ChevronRight size={13} className="text-[#a8a49c] dark:text-[#555]" />
              <span className="font-bold text-[#1a1917] dark:text-[#f5f4f0] text-sm tracking-tight">
                {pageTitle}
              </span>
            </div>
          </div>

          {/* Right: Date & User Identity */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-[#78716c] dark:text-[#a8a49c] font-medium font-mono">
              <Calendar size={13} className="text-[#8c867e]" />
              <span>{todayFormatted}</span>
            </div>

            <div className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1 rounded-full bg-[#f7f6f3] dark:bg-[#1f1e1c] border border-[#e4e2dc] dark:border-[#2c2a27]">
              <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-blue-500/30 bg-slate-100 dark:bg-slate-800 flex items-center justify-center shadow-xs">
                <img src={adminAvatar} alt="Admin" className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col text-left justify-center py-0.5 min-w-0">
                <span className="text-xs font-semibold text-[#1a1917] dark:text-[#f5f4f0] leading-normal pt-0.5 truncate max-w-[140px] block">
                  {operatorName}
                </span>
                <span className="text-[10px] text-[#8c827a] font-medium leading-normal block -mt-0.5">
                  Manager
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3.5 sm:p-4 lg:p-5 pb-4 lg:pb-6">

          <div className="animate-fade-in">
            {/* Banner */}
            <AnimatePresence>
              {banner && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
                  <div className={`px-4 py-3 rounded-xl flex items-start gap-3 border ${bc.wrap}`}>
                    <Megaphone size={14} className={`${bc.icon} shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-bold uppercase tracking-wide mb-0.5 ${bc.label}`}>
                        {banner.type === 'urgent' ? 'Important' : banner.type === 'warning' ? 'Notice' : banner.type === 'success' ? 'Update' : 'Announcement'}
                      </p>
                      <p className="text-sm text-[#1a1917] dark:text-[#f5f4f0] leading-relaxed">{banner.message}</p>
                    </div>
                    <button onClick={() => { setBanner(null); setBannerDismissed(banner.createdAt); localStorage.setItem('lg_banner_dismissed', banner.createdAt); }}
                      className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 hover:bg-black/5 transition-colors">
                      <X size={10} className="text-[#6b6860]" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Suspense fallback={<PageSkeletonFallback type="admin" />}>
              <motion.div key={location.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
                <Outlet />
              </motion.div>
            </Suspense>
          </div>
        </main>

        <AppFooter type="admin" />
      </div>

      {/* ── Enterprise Mobile Side Navigation Drawer (Off-Canvas Right Drawer) ── */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {moreOpen && (
            <motion.div
              key="drawer-overlay-wrapper"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] lg:hidden overflow-hidden"
            >
              {/* Backdrop */}
              <motion.div
                key="drawer-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={closeDrawer}
                className="absolute inset-0 bg-black/60 backdrop-blur-xs"
              />

              {/* Slide Drawer Panel (Anchored to LEFT Edge - Opposite of More button) */}
              <motion.aside
                key="drawer-panel"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 320 }}
                style={{ left: 0, right: 'auto' }}
                className="absolute top-0 bottom-0 left-0 right-auto z-10 w-[280px] max-w-[78vw] h-full bg-white dark:bg-[#121110] border-r border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col justify-between overflow-hidden"
              >
                {/* Top Clean Header */}
                <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800/80 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2 border-blue-500/30 bg-slate-100 dark:bg-slate-800 flex items-center justify-center shadow-sm">
                      <img src={adminAvatar} alt="Admin" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-bold text-gray-900 dark:text-gray-100 truncate leading-normal pt-1 pb-0.5">
                        {operatorName}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Admin Console</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeDrawer}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Seamless Flat Navigation List (No clutter or boxed borders) */}
                <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-3">
                  {moreCategories.map((group, idx) => (
                    <div key={group.category}>
                      {idx > 0 && <div className="h-px bg-gray-100 dark:bg-gray-800/60 my-2 mx-1" />}
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-3 py-1">
                        {group.category}
                      </p>
                      <div className="space-y-0.5">
                        {group.links.map((link) => {
                          const isActive = link.end
                            ? location.pathname === link.to
                            : location.pathname === link.to || (link.to !== '/admin' && location.pathname.startsWith(link.to));
                          const isSupport = link.to === '/admin/support';
                          const hasUnresolved = isSupport && unresolvedTickets > 0;
                          return (
                            <button
                              key={link.to}
                              onClick={() => { closeDrawer(); navigate(link.to); }}
                              className={`w-full group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors touch-manipulation ${
                                isActive
                                  ? 'bg-[#1e3a5f] text-white font-semibold shadow-xs'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-white/5 active:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <link.Icon
                                  size={18}
                                  strokeWidth={isActive ? 2.2 : 1.8}
                                  className={isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}
                                />
                                <div className="text-left min-w-0">
                                  <p className="text-[13px] font-medium tracking-tight truncate leading-tight">
                                    {link.label}
                                  </p>
                                  {link.desc && (
                                    <p className={`text-[10px] truncate leading-tight mt-0.5 ${isActive ? 'text-blue-100/80' : 'text-gray-400 dark:text-gray-500'}`}>
                                      {link.desc}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {hasUnresolved && (
                                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full bg-amber-500 text-white font-mono shadow-xs animate-pulse leading-none">
                                    {unresolvedTickets > 99 ? '99+' : unresolvedTickets}
                                  </span>
                                )}
                                <ChevronRight
                                  size={14}
                                  className={`transition-transform shrink-0 ${
                                    isActive ? 'text-white/80' : 'text-gray-400 dark:text-gray-500 group-hover:translate-x-0.5'
                                  }`}
                                />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Dev Panel Link */}
                  {isDev && (
                    <div>
                      <div className="h-px bg-gray-100 dark:bg-gray-800/60 my-2 mx-1" />
                      <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-500 px-3 py-1">
                        Developer
                      </p>
                      <button
                        onClick={() => { closeDrawer(); window.location.href = '/dev'; }}
                        className="w-full group flex items-center justify-between px-3 py-2.5 rounded-xl text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-sm transition-colors touch-manipulation"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Code size={18} strokeWidth={1.8} />
                          <span className="text-[13px] font-medium tracking-tight">Dev Panel</span>
                        </div>
                        <ChevronRight size={14} className="text-emerald-500/70 group-hover:translate-x-0.5 transition-transform shrink-0" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Bottom Clean Footer */}
                <div className="p-3 border-t border-gray-100 dark:border-gray-800/80 shrink-0">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 text-sm font-semibold transition-colors"
                  >
                    <LogOut size={17} strokeWidth={1.8} />
                    <span>Logout Session</span>
                  </button>
                  <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 pt-2 font-medium">
                    Lucy Garden Console · v{APP_CONFIG.version}
                  </p>
                </div>
              </motion.aside>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* ── Mobile Bottom Navigation (Grounded Enterprise Toolbar) ── */}
      <nav className={`fixed bottom-0 left-0 right-0 w-full z-40 lg:hidden transition-transform duration-300 ${navHidden ? 'translate-y-full' : 'translate-y-0'} bg-white dark:bg-[#121110] border-t border-[#e2e0db] dark:border-[#262524] shadow-[0_-2px_10px_rgba(0,0,0,0.03)] pb-[env(safe-area-inset-bottom)]`}>
        <div className="flex items-center justify-around h-[54px] px-1">
          {bottomNavLinks.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} className="relative flex-1 flex justify-center py-1">
              {({ isActive }) => (
                <div className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${isActive ? 'text-[#1e3a5f] dark:text-[#60a5fa]' : 'text-[#64748b] dark:text-[#94a3b8] hover:text-[#1a1917]'}`}>
                  {/* Subtle top indicator line on active tab */}
                  {isActive && (
                    <motion.div
                      layoutId="adminBottomActiveLine"
                      className="absolute -top-[5px] w-6 h-[2.5px] bg-[#1e3a5f] dark:bg-[#60a5fa] rounded-full"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <item.Icon size={20} strokeWidth={isActive ? 2.2 : 1.7} />
                  <span className={`text-[10px] tracking-tight ${isActive ? 'font-bold' : 'font-medium'}`}>
                    {item.label}
                  </span>
                </div>
              )}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(prev => !prev)}
            className="relative flex-1 flex justify-center py-1"
          >
            <div className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${moreOpen ? 'text-[#1e3a5f] dark:text-[#60a5fa]' : 'text-[#64748b] dark:text-[#94a3b8] hover:text-[#1a1917]'}`}>
              {moreOpen && (
                <motion.div
                  layoutId="adminBottomActiveLine"
                  className="absolute -top-[5px] w-6 h-[2.5px] bg-[#1e3a5f] dark:bg-[#60a5fa] rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <div className="relative flex items-center justify-center">
                <MoreHorizontal size={20} strokeWidth={moreOpen ? 2.2 : 1.7} />
                {unresolvedTickets > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-white dark:ring-[#121110] animate-pulse" />
                )}
              </div>
              <span className={`text-[10px] tracking-tight ${moreOpen ? 'font-bold' : 'font-medium'}`}>
                More
              </span>
            </div>
          </button>
        </div>
      </nav>

    </div>
  );
}
